// Libs
import {get, Json} from '@packages/utilities/json';
import {pair} from '@packages/utilities/Pair';
import {emptyObject} from '@packages/utilities/fp/utils';
import {unionWith} from '@packages/utilities/key-map';
import {extractStyleLessBackgroundImageIds} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import {
  Plugins,
  Expressions,
  Component,
  matchExpression,
} from '@packages/systems/wfdl';

import {
  namesEqual,
  qualify,
  typeName,
  getName,
} from '@packages/systems/wfdl-Qualified';

// Types
import type {RawStyleBlockServerType} from '@packages/systems/core/utils/StyleTypes';

type AssetId = string;
type Context = Partial<{
  parentElementId: string;
}>;

type NodeAssetUsage = {
  [assetId: AssetId]: Array<string>;
};

type ComponentNodeAssetUsage = {
  [componentId: string]: NodeAssetUsage;
};

type StyleBlockAssetUsage = {
  [styleBlockId: string]: Set<AssetId>;
};

const assetAssetType = qualify(Plugins.name('Asset'), typeName('Asset'));
const assetImageType = qualify(Plugins.name('Asset'), typeName('Image'));
const basicLinkType = qualify(Plugins.name('Basic'), typeName('Link'));
const animationLottiePathType = qualify(
  Plugins.name('Animation'),
  typeName('LottiePath')
);

/**
 * Returns usages of asset IDs in page and component nodes.
 */
export const getNodeAssetUsage = (
  componentMap: Component.ComponentMap
): {
  page: NodeAssetUsage;
  components: ComponentNodeAssetUsage;
} => {
  const generateAssetUsage = Expressions.everythingWithContext<Context>(
    emptyObject
  )<NodeAssetUsage>(unionWith(combine))(
    matchExpression({
      Element: (element) => () => {
        const {id} = element;

        const context = {
          parentElementId: id,
        };

        return pair(context, emptyObject);
      },
      Literal:
        ({name, value}) =>
        (context) => {
          if (!value) {
            return pair(context, emptyObject);
          }

          if (
            namesEqual(name, assetImageType) ||
            namesEqual(name, assetAssetType)
          ) {
            return getUpdatedPair(context)(get(value, ['id']));
          }

          if (
            namesEqual(name, basicLinkType) &&
            get(value, ['mode']) === 'file'
          ) {
            return getUpdatedPair(context)(get(value, ['assetId']));
          }

          if (namesEqual(name, animationLottiePathType)) {
            return getUpdatedPair(context)(
              extractAssetId(String(get(value, ['src'])))
            );
          }

          return pair(context, emptyObject);
        },
      _: () => (context) => {
        return pair(context, emptyObject);
      },
    })
  );

  return componentMap.reduce<{
    page: NodeAssetUsage;
    components: ComponentNodeAssetUsage;
  }>(
    (acc, componentType, qualifiedComponentId) => {
      const component = Component.getRender(componentType);
      const assetUsage = generateAssetUsage(component);
      const componentId = getName(qualifiedComponentId);
      if (componentId === 'page') {
        acc.page = assetUsage;
      } else {
        acc.components[componentId] = assetUsage;
      }

      return acc;
    },
    {
      page: {},
      components: {},
    }
  );
};

/**
 * Returns usages of asset IDs in style blocks.
 */
export const getStyleBlockAssetUsage = (
  styleBlocks: Array<RawStyleBlockServerType>
): {
  styleBlocks: StyleBlockAssetUsage;
} => {
  const styles: StyleBlockAssetUsage = {};
  for (const styleBlock of styleBlocks) {
    const styleLess = styleBlock.data?.styleLess;
    const assetIds = new Set<string>();
    if (typeof styleLess === 'string') {
      extractStyleLessBackgroundImageIds(styleLess)
        .filter(isValidAssetId)
        .forEach((assetId) => {
          assetIds.add(assetId);
        });
    }

    const variants = styleBlock.data?.variants;
    if (variants) {
      for (const variant of Object.values(variants)) {
        if (typeof variant.styleLess !== 'string') {
          continue;
        }

        extractStyleLessBackgroundImageIds(variant.styleLess)
          .filter(isValidAssetId)
          .forEach((assetId) => {
            assetIds.add(assetId);
          });
      }
    }

    if (assetIds.size === 0) {
      continue;
    }

    styles[styleBlock._id] = assetIds;
  }

  return {
    styleBlocks: styles,
  };
};

/**
 * Aggregates asset usage across a component. The result
 * typically contains only one or a few asset IDs. The rare
 * case for an asset used multiple times in the same element
 * is also covered.
 */
const combine =
  (a: Array<string>) =>
  (b: Array<string>): Array<string> => {
    const combined = a.concat(b);
    return combined.length > 1 ? [...new Set(combined)] : combined;
  };

const getUpdatedPair =
  (context: Context) =>
  (maybeAssetId?: Json): [Context, NodeAssetUsage] =>
    pair(
      context,
      isValidAssetId(maybeAssetId)
        ? {[maybeAssetId]: [context.parentElementId]}
        : emptyObject
    );

/**
 * Filter out placeholders and any other invalid asset IDs.
 */
const isValidAssetId = (assetId: unknown): assetId is string =>
  typeof assetId === 'string' && isObjectId(assetId);

/**
 * Using the following the function to extract the asset ID from
 * a `src` URL instead of `parseAssetUrl` in asset utils because
 * it imports `entrypoints/server/lib` at time of writing. Shared
 * packages shouldn't import all of that bloat.
 *
 * The logic matches URLs that end like the following:
 * .../<assetId>_<assetname>.<extension>
 *
 * @see https://github.com/webflow/webflow/blob/4f49e1890fbaa36b8bfe0d3e17acf71296ee6e7e/entrypoints/server/models/asset.js#L795-L812
 * @see https://github.com/webflow/webflow/blob/dev/entrypoints/server/lib/assetUtils.js#L183-L240
 */
const extractAssetId = (src: string): string | null => {
  const lastSlashIndex = src.lastIndexOf('/');
  if (lastSlashIndex === -1) {
    return null;
  }

  const assetIdPart = src.substring(lastSlashIndex + 1);
  const underscoreIndex = assetIdPart.indexOf('_');
  if (underscoreIndex === -1) {
    return null;
  }

  return assetIdPart.substring(0, underscoreIndex);
};

/**
 * This function is inlined since this is a universal
 * package and `packages/utilities/mongoUtils` is a
 * server-only package.
 */
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
const isObjectId = (maybeId: unknown): boolean =>
  OBJECT_ID_RE.test(String(maybeId));
